/**
 * FairAssessmentTab Component
 *
 * A comprehensive view of Bayesian Fair Scores with detailed explanations,
 * methodology breakdown, and visual analysis of bias corrections.
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Scale,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  AlertCircle,
  CheckCircle,
  Shield,
  BarChart3,
  Target,
  Users,
  MapPin,
  Truck,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Activity,
  Layers,
} from 'lucide-react';
import { carrierApi } from '../../services/api';
import type { FairScoreResponse, FairScore, BASICScore } from '../../types/api';

interface FairAssessmentTabProps {
  dotNumber: number;
  basics: BASICScore[];
  fleetSize?: number;
  state?: string;
}

const BASIC_FULL_NAMES: Record<string, { name: string; description: string }> = {
  'BASIC-11': {
    name: 'Unsafe Driving',
    description: 'Operation of vehicles in a dangerous or careless manner (speeding, improper lane changes, etc.)',
  },
  'BASIC-12': {
    name: 'Hours-of-Service Compliance',
    description: 'Operating beyond regulated driving limits, failing to take required breaks',
  },
  'BASIC-13': {
    name: 'Driver Fitness',
    description: 'Operating without proper licensing, medical certification, or training',
  },
  'BASIC-14': {
    name: 'Controlled Substances/Alcohol',
    description: 'Use or possession of controlled substances or alcohol while operating',
  },
  'BASIC-15': {
    name: 'Vehicle Maintenance',
    description: 'Failure to maintain vehicles properly (brakes, lights, tires, etc.)',
  },
  'BASIC-16': {
    name: 'Hazardous Materials Compliance',
    description: 'Improper handling, labeling, or transport of hazardous materials',
  },
  'BASIC-17': {
    name: 'Crash Indicator',
    description: 'Historical crash involvement patterns and severity',
  },
};

const FLEET_SIZE_LABELS: Record<string, string> = {
  SMALL: '1-9 Power Units',
  MEDIUM: '10-49 Power Units',
  LARGE: '50-199 Power Units',
  MEGA: '200+ Power Units',
};

const getScoreGrade = (score: number): { grade: string; color: string; bg: string } => {
  if (score >= 90) return { grade: 'Excellent', color: 'var(--color-success)', bg: 'rgba(16, 185, 129, 0.1)' };
  if (score >= 75) return { grade: 'Good', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' };
  if (score >= 50) return { grade: 'Average', color: 'var(--color-warning)', bg: 'rgba(245, 158, 11, 0.1)' };
  if (score >= 25) return { grade: 'Below Average', color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' };
  return { grade: 'Poor', color: 'var(--color-danger)', bg: 'rgba(239, 68, 68, 0.1)' };
};

const getConfidenceInfo = (level: string) => {
  const info: Record<string, { color: string; bg: string; description: string }> = {
    HIGH: {
      color: 'var(--color-success)',
      bg: 'rgba(16, 185, 129, 0.1)',
      description: 'Narrow confidence interval (<15 points). High certainty in the estimate.',
    },
    MEDIUM: {
      color: 'var(--color-warning)',
      bg: 'rgba(245, 158, 11, 0.1)',
      description: 'Moderate confidence interval (15-30 points). Reasonable certainty.',
    },
    LOW: {
      color: 'var(--color-danger)',
      bg: 'rgba(239, 68, 68, 0.1)',
      description: 'Wide confidence interval (>30 points). Limited data available.',
    },
  };
  return info[level] || info.MEDIUM;
};

/**
 * Round number to 1 decimal place, handling floating point precision
 * FIX #9: Prevents display of floating point garbage like "7.3824000000000005"
 */
const roundToOne = (num: number | null | undefined): string => {
  if (num === null || num === undefined || isNaN(num)) return '-';
  return Number(num.toFixed(1)).toString();
};

export const FairAssessmentTab: React.FC<FairAssessmentTabProps> = ({
  dotNumber,
  basics,
  fleetSize,
  state,
}) => {
  const [fairScores, setFairScores] = useState<FairScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedBasic, setExpandedBasic] = useState<string | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);

  useEffect(() => {
    const fetchFairScores = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await carrierApi.getFairScores(dotNumber);
        setFairScores(response);
      } catch (err: unknown) {
        if (err instanceof Error && (err as any).response?.status === 404) {
          setFairScores(null);
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load FAIR assessment';
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    if (dotNumber) {
      fetchFairScores();
    }
  }, [dotNumber]);

  // Create comparison data - use fair scores as primary source
  const comparisonData = useMemo(() => {
    if (!fairScores || !fairScores.scores) return [];

    // Create a map of SMS scores for lookup.
    // Backend returns items shaped as { basicsType: { basicsCode, basicsId }, basicsPercentile }.
    // basicsCode is the human name (e.g. "Unsafe Driving"), basicsId is the numeric id (11-17).
    // basicsPercentile may be the literal "Not Public" for FMCSA-suppressed BASICs.
    const smsScoreMap = new Map<string, { percentile: number | null; notPublic: boolean }>();
    (basics || []).forEach((basic: any) => {
      const code: string | undefined =
        basic?.basicsType?.basicsCode && /^BASIC-\d+$/.test(basic.basicsType.basicsCode)
          ? basic.basicsType.basicsCode
          : basic?.basicsType?.basicsId != null
            ? `BASIC-${basic.basicsType.basicsId}`
            : basic?.basic_id
              ? (typeof basic.basic_id === 'string' && basic.basic_id.startsWith('BASIC-')
                  ? basic.basic_id
                  : `BASIC-${basic.basic_id}`)
              : undefined;
      if (!code) return;
      const raw = basic?.basicsPercentile ?? basic?.percentile;
      if (raw === 'Not Public') {
        smsScoreMap.set(code, { percentile: null, notPublic: true });
      } else if (raw === null || raw === undefined || raw === '') {
        smsScoreMap.set(code, { percentile: null, notPublic: false });
      } else {
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
        smsScoreMap.set(code, { percentile: isNaN(n) ? null : n, notPublic: false });
      }
    });

    // Use fair scores as primary source - show all calculated fair scores
    return fairScores.scores.map(score => {
      const basicInfo = BASIC_FULL_NAMES[score.basicId];
      const smsData = smsScoreMap.get(score.basicId);
      const smsPercentile = smsData?.percentile ?? null;
      const smsNotPublic = smsData?.notPublic ?? false;

      return {
        basicId: score.basicId,
        name: basicInfo?.name || score.basicId,
        description: basicInfo?.description || '',
        smsPercentile,
        smsNotPublic,
        smsStatus: smsPercentile !== null
          ? smsPercentile >= 75 ? 'Alert' : smsPercentile >= 65 ? 'Warning' : 'Good'
          : null,
        fairScore: score.fairScore,
        fairScoreLower: score.fairScoreLower,
        fairScoreUpper: score.fairScoreUpper,
        confidence: score.confidenceLevel,
        thetaMean: score.thetaMean,
        thetaStd: score.thetaStd,
        biasAdjustments: score.biasAdjustments ?? null,
        peerGroup: score.peerGroup ?? null,
        convergence: score.convergence ?? null,
      };
    });
  }, [basics, fairScores]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 'var(--space-md)',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            border: '4px solid var(--color-gray-200)',
            borderTopColor: 'var(--color-primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ color: 'var(--color-gray-500)' }}>Loading Fair Assessment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="card-modern"
        style={{
          padding: 'var(--space-xl)',
          textAlign: 'center',
          background: 'rgba(239, 68, 68, 0.05)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}
      >
        <AlertCircle size={48} color="var(--color-danger)" style={{ marginBottom: 'var(--space-md)' }} />
        <h3 style={{ margin: '0 0 var(--space-sm)', color: 'var(--color-danger)' }}>Error Loading Data</h3>
        <p style={{ color: 'var(--color-gray-600)' }}>{error}</p>
      </div>
    );
  }

  if (!fairScores || fairScores.scores.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
        {/* No Data Banner */}
        <div
          className="card-modern"
          style={{
            padding: 'var(--space-xl)',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.2)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--color-primary) 0%, #9333ea 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-lg)',
            }}
          >
            <Scale size={40} color="white" />
          </div>
          <h2 style={{ margin: '0 0 var(--space-sm)', color: 'var(--color-gray-900)' }}>
            Fair Assessment Not Yet Available
          </h2>
          <p style={{ margin: '0 0 var(--space-lg)', color: 'var(--color-gray-600)', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Bayesian fair scores for DOT {dotNumber} have not been calculated yet. Our system processes carriers
            periodically using advanced statistical methods to provide bias-corrected safety assessments.
          </p>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-xs)',
              padding: 'var(--space-sm) var(--space-md)',
              background: 'white',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-gray-200)',
              fontSize: '0.875rem',
              color: 'var(--color-gray-600)',
            }}
          >
            <Info size={16} />
            Check back soon for updated fair scores
          </div>
        </div>

        {/* What is Fair Assessment */}
        <div className="card-modern" style={{ padding: 'var(--space-lg)' }}>
          <h3 style={{ margin: '0 0 var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <HelpCircle size={20} color="var(--color-primary)" />
            What is Fair Assessment?
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-md)' }}>
            <div style={{ padding: 'var(--space-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <Users size={18} color="var(--color-primary)" />
                <strong>Fleet Size Correction</strong>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                Small carriers may appear worse due to statistical noise. We adjust for fleet size to provide fair comparisons.
              </p>
            </div>
            <div style={{ padding: 'var(--space-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <MapPin size={18} color="var(--color-primary)" />
                <strong>Regional Adjustment</strong>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                Inspection rates vary by region. We account for these differences to ensure fair cross-regional comparison.
              </p>
            </div>
            <div style={{ padding: 'var(--space-md)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <Truck size={18} color="var(--color-primary)" />
                <strong>Operation Type</strong>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                Interstate, intrastate, and hazmat operations face different scrutiny. We normalize for operation type.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      {/* Header Summary */}
      <div
        className="card-modern"
        style={{
          padding: 'var(--space-xl)',
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(147, 51, 234, 0.03) 100%)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
          {/* Overall Score */}
          <div style={{ textAlign: 'center', minWidth: '200px' }}>
            <div
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: `conic-gradient(${getScoreGrade(fairScores.summary.overallFairScore || 0).color} ${(fairScores.summary.overallFairScore || 0) * 3.6}deg, var(--color-gray-200) 0deg)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto var(--space-md)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ fontSize: '2rem', fontWeight: 700, color: getScoreGrade(fairScores.summary.overallFairScore || 0).color }}>
                  {roundToOne(fairScores.summary.overallFairScore) !== '-' ? roundToOne(fairScores.summary.overallFairScore) : 'N/A'}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', textTransform: 'uppercase' }}>
                  Fair Score
                </span>
              </div>
            </div>
            <div
              style={{
                display: 'inline-block',
                padding: 'var(--space-xs) var(--space-md)',
                background: getScoreGrade(fairScores.summary.overallFairScore || 0).bg,
                color: getScoreGrade(fairScores.summary.overallFairScore || 0).color,
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {getScoreGrade(fairScores.summary.overallFairScore || 0).grade}
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <h3 style={{ margin: '0 0 var(--space-md)', fontSize: '1.1rem' }}>Assessment Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-md)' }}>
              <div className="stat-box">
                <div className="stat-label">BASICs Analyzed</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>
                  {fairScores.scores.length}
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">High Confidence</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)' }}>
                  {fairScores.summary.highConfidenceCount}
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">Medium Confidence</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)' }}>
                  {fairScores.summary.mediumConfidenceCount}
                </div>
              </div>
              <div className="stat-box">
                <div className="stat-label">Avg CI Width</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-700)' }}>
                  {roundToOne(fairScores.summary.averageCredibleIntervalWidth)}pt
                </div>
              </div>
            </div>

            {/* Peer Group Info */}
            {fairScores.scores[0]?.peerGroup && (
              <div
                style={{
                  marginTop: 'var(--space-md)',
                  padding: 'var(--space-md)',
                  background: 'white',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-gray-200)',
                }}
              >
                <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', marginBottom: 'var(--space-xs)', textTransform: 'uppercase' }}>
                  Peer Group Classification
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                    <Users size={14} color="var(--color-gray-500)" />
                    {FLEET_SIZE_LABELS[fairScores.scores[0].peerGroup.fleetSizeCategory] || fairScores.scores[0].peerGroup.fleetSizeCategory}
                  </span>
                  {fairScores.scores[0].peerGroup.fmcsaRegion && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                      <MapPin size={14} color="var(--color-gray-500)" />
                      Region {fairScores.scores[0].peerGroup.fmcsaRegion}
                    </span>
                  )}
                  {fairScores.scores[0].peerGroup.segment && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                      <Truck size={14} color="var(--color-gray-500)" />
                      {fairScores.scores[0].peerGroup.segment}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Methodology Toggle */}
      <button
        onClick={() => setShowMethodology(!showMethodology)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: 'var(--space-md)',
          background: 'var(--color-gray-50)',
          border: '1px solid var(--color-gray-200)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: 500,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <Layers size={18} color="var(--color-primary)" />
          How Fair Scores Work (Methodology)
        </span>
        {showMethodology ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {showMethodology && (
        <div
          className="card-modern"
          style={{
            padding: 'var(--space-lg)',
            marginTop: 'calc(var(--space-lg) * -1 + var(--space-sm))',
            borderTop: 'none',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-lg)' }}>
            <div>
              <h4 style={{ margin: '0 0 var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <Activity size={16} color="var(--color-primary)" />
                Bayesian Item Response Theory (IRT)
              </h4>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                We use a hierarchical 1-Parameter Logistic (1PL) IRT model to estimate carrier safety.
                This treats each violation type as an "item" and estimates the underlying safety ability (theta)
                for each carrier while accounting for how common each violation type is.
              </p>
            </div>
            <div>
              <h4 style={{ margin: '0 0 var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <Target size={16} color="var(--color-primary)" />
                Credible Intervals
              </h4>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                Unlike point estimates, we provide 90% credible intervals showing the range where the true
                score likely falls. Wider intervals indicate more uncertainty (often due to limited inspection data).
                The confidence level (HIGH/MEDIUM/LOW) is based on this interval width.
              </p>
            </div>
            <div>
              <h4 style={{ margin: '0 0 var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <Users size={16} color="var(--color-primary)" />
                Hierarchical Bias Correction
              </h4>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                The model includes hierarchical priors for fleet size, region, and operation type.
                This "shrinks" extreme estimates toward group means, particularly helpful for small carriers
                with limited data who might otherwise appear artificially good or bad.
              </p>
            </div>
            <div>
              <h4 style={{ margin: '0 0 var(--space-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                <BarChart3 size={16} color="var(--color-primary)" />
                Score Interpretation
              </h4>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                Fair Scores range 0-100 where <strong>higher is safer</strong>. A score of 75 means the carrier
                is safer than 75% of comparable carriers after bias correction. This is the opposite of FMCSA
                percentiles where higher numbers indicate worse performance.
              </p>
            </div>
          </div>

          <div
            style={{
              marginTop: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'rgba(37, 99, 235, 0.05)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid rgba(37, 99, 235, 0.1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
              <Info size={18} color="var(--color-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                <strong>Model Details:</strong> {fairScores.metadata.modelType} |
                Last Updated: {fairScores.metadata.lastUpdated ? new Date(fairScores.metadata.lastUpdated).toLocaleDateString() : 'N/A'} |
                MCMC Convergence: {fairScores.scores[0]?.convergence?.status || 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed BASIC Breakdown */}
      <div className="card-modern" style={{ padding: 'var(--space-lg)' }}>
        <h3 style={{ margin: '0 0 var(--space-lg)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <BarChart3 size={20} color="var(--color-primary)" />
          BASIC-by-BASIC Analysis
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {comparisonData.map(comp => {
            const isExpanded = expandedBasic === comp.basicId;
            const hasData = comp.fairScore !== null;
            const grade = hasData ? getScoreGrade(comp.fairScore!) : null;

            return (
              <div
                key={comp.basicId}
                style={{
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  background: hasData ? 'white' : 'var(--color-gray-50)',
                }}
              >
                {/* Header Row */}
                <button
                  onClick={() => setExpandedBasic(isExpanded ? null : comp.basicId)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: 'var(--space-md)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>{comp.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>{comp.basicId}</div>
                  </div>

                  {/* FMCSA Score */}
                  <div style={{ textAlign: 'center', minWidth: '80px', marginRight: 'var(--space-lg)' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--color-gray-500)', marginBottom: '2px' }}>FMCSA</div>
                    {comp.smsPercentile !== null ? (
                      <span
                        style={{
                          fontWeight: 600,
                          color: comp.smsPercentile >= 75 ? 'var(--color-danger)' : comp.smsPercentile >= 65 ? 'var(--color-warning)' : 'var(--color-success)',
                        }}
                      >
                        {comp.smsPercentile}%ile
                      </span>
                    ) : comp.smsNotPublic ? (
                      <span style={{ color: 'var(--color-gray-500)', fontSize: '0.75rem', fontStyle: 'italic' }}>Not Public</span>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>N/A</span>
                    )}
                  </div>

                  {/* Fair Score */}
                  <div style={{ textAlign: 'center', minWidth: '100px', marginRight: 'var(--space-lg)' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--color-gray-500)', marginBottom: '2px' }}>Fair Score</div>
                    {hasData ? (
                      <div>
                        <span style={{ fontWeight: 700, color: grade!.color, fontSize: '1.1rem' }}>
                          {roundToOne(comp.fairScore)}
                        </span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--color-gray-400)', marginLeft: '4px' }}>
                          ({roundToOne(comp.fairScoreLower)}-{roundToOne(comp.fairScoreUpper)})
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                    )}
                  </div>

                  {/* Confidence */}
                  <div style={{ textAlign: 'center', minWidth: '80px', marginRight: 'var(--space-md)' }}>
                    {comp.confidence && (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          background: getConfidenceInfo(comp.confidence).bg,
                          color: getConfidenceInfo(comp.confidence).color,
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {comp.confidence}
                      </span>
                    )}
                  </div>

                  {hasData && (isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
                </button>

                {/* Expanded Details */}
                {isExpanded && hasData && (
                  <div
                    style={{
                      padding: 'var(--space-md)',
                      borderTop: '1px solid var(--color-gray-200)',
                      background: 'var(--color-gray-50)',
                    }}
                  >
                    <p style={{ margin: '0 0 var(--space-md)', fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                      {comp.description}
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
                      {/* Statistical Details */}
                      <div style={{ padding: 'var(--space-sm)', background: 'white', borderRadius: 'var(--radius-sm)' }}>
                        <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', marginBottom: '4px' }}>
                          Statistical Parameters
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>
                          <div>Theta (ability): <strong>{comp.thetaMean?.toFixed(2)}</strong> ± {comp.thetaStd?.toFixed(2)}</div>
                        </div>
                      </div>

                      {/* Confidence Details */}
                      <div style={{ padding: 'var(--space-sm)', background: 'white', borderRadius: 'var(--radius-sm)' }}>
                        <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', marginBottom: '4px' }}>
                          Confidence Level
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>
                          {getConfidenceInfo(comp.confidence!).description}
                        </div>
                      </div>

                      {/* Convergence */}
                      {comp.convergence && (
                        <div style={{ padding: 'var(--space-sm)', background: 'white', borderRadius: 'var(--radius-sm)' }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-500)', marginBottom: '4px' }}>
                            Model Convergence
                          </div>
                          <div style={{ fontSize: '0.85rem' }}>
                            <div>Rhat: <strong>{comp.convergence.rhat.toFixed(3)}</strong></div>
                            <div>ESS: <strong>{comp.convergence.ess}</strong></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="card-modern" style={{ padding: 'var(--space-lg)' }}>
        <h3 style={{ margin: '0 0 var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <HelpCircle size={20} color="var(--color-primary)" />
          How to Interpret Your Fair Scores
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--space-md)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
            <CheckCircle size={18} color="var(--color-success)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Score 75-100: Excellent/Good</strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--color-gray-600)' }}>
                Carrier performs better than most peers after bias correction. Low intervention risk.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
            <AlertCircle size={18} color="var(--color-warning)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Score 25-74: Average/Below Average</strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--color-gray-600)' }}>
                Performance is typical or somewhat below peers. May warrant monitoring.
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
            <AlertCircle size={18} color="var(--color-danger)" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              <strong style={{ fontSize: '0.9rem' }}>Score 0-24: Poor</strong>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--color-gray-600)' }}>
                Significantly worse than peers even after bias correction. High intervention risk.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FairAssessmentTab;
